import React, { useState } from 'react';
import { Box, Card, CardContent, Container, Grid, Typography, FormControl, InputLabel, Select, MenuItem, FormGroup, FormControlLabel, Checkbox, Stack, useMediaQuery, useTheme, Button, Backdrop, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ComposedChart } from 'recharts';
import { blueGrey, green, grey, orange, red } from '@mui/material/colors';

import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const COLORS = [red[500], orange[500], grey[500], green[500], blueGrey[500]];

export default function LeadsCharts({ dados, leadsAll, operadores, setOperadores, account }) {
  const [selectedOperador, setSelectedOperador] = useState('');
  const [periodoSelecionado, setPeriodoSelecionado] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const isAdmin = account.conta === account.id
  if (!isAdmin) {
    if (!selectedOperador) setSelectedOperador(account.id)
  }

  if (!dados || !leadsAll.length) return null;

  if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) { console.log('Leads Charts', dados, leadsAll); }
  const getFilteredLeads = () => {
    let filteredLeads = leadsAll;

    // Filtro por operador
    if (selectedOperador) {
      filteredLeads = filteredLeads.filter(lead => {
        return lead.idoperador === selectedOperador;
      });
    }

    // Filtro por período
    if (periodoSelecionado) {
      filteredLeads = filteredLeads.filter(lead => {
        const leadDate = new Date(lead.dtdesignacao);
        const hoje = new Date();
        let dataInicio;

        switch (periodoSelecionado) {
          case 'ultimos30':
            dataInicio = new Date();
            dataInicio.setDate(hoje.getDate() - 30);
            return leadDate >= dataInicio && leadDate <= hoje;

          case 'mesAtual':
            dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
            return leadDate >= dataInicio && leadDate <= fimMes;

          case 'trimestre':
            dataInicio = new Date();
            dataInicio.setDate(hoje.getDate() - 90);
            return leadDate >= dataInicio && leadDate <= hoje;
          default:
            return true;
        }
      });
    }

    return filteredLeads;
  };

  const handlePeriodoChange = (evento) => {
    const valor = evento.target.name;
    setPeriodoSelecionado(periodoSelecionado === valor ? '' : valor);
  };

  const handleOperadorChange = (event) => {
    setSelectedOperador(event.target.value);
    // console.log('Operador selecionado:', event.target.value);
  };

  const getPeriodoTexto = () => {
    switch (periodoSelecionado) {
      case 'ultimos30':
        return 'Últimos 30 dias';
      case 'mesAtual':
        return 'Mês atual';
      case 'trimestre':
        return 'Últimos 90 dias';
      default:
        return '';
    }
  };

  const getStatusData = () => {
    const filteredLeads = getFilteredLeads();

    // Sempre usa os leads filtrados para manter consistência
    if (selectedOperador || periodoSelecionado || filteredLeads.length !== leadsAll.length) {
      // Recalcula as fases baseado nos leads filtrados
      const faseCount = {};
      filteredLeads.forEach(lead => {
        const fase = lead.fase || 'Sem fase';
        faseCount[fase] = (faseCount[fase] || 0) + 1;
      });

      return Object.entries(faseCount).map(([fase, count]) => ({
        name: fase,
        value: count
      }));
    }

    // Usa dados originais apenas se nenhum filtro estiver aplicado
    return dados?.map(d => ({
      name: d.fase,
      value: d.total
    })) || [];
  };

  const getSituacaoData = () => {
    const filteredLeads = getFilteredLeads();

    // Sempre usa os leads filtrados para manter consistência
    if (selectedOperador || periodoSelecionado || filteredLeads.length !== leadsAll.length) {
      // Recalcula as situações baseado nos leads filtrados
      const situacaoCount = {};
      filteredLeads.forEach(lead => {
        if (lead.situacao || lead.motivo) {
          const situacao = lead.situacao || lead.motivo || 'Outros';
          situacaoCount[situacao] = (situacaoCount[situacao] || 0) + 1;
        }
      });

      return Object.entries(situacaoCount).map(([situacao, count]) => ({
        name: situacao,
        value: count
      }));
    }

    // Usa dados originais apenas se nenhum filtro estiver aplicado
    return dados?.encerramento?.map(d => ({
      name: d.motivo,
      value: d.qtd
    })) || [];
  };

  const qtdOperador = () => {
    // localiza nome null em dados.operador
    let _operador = dados?.operador || []
    if (_operador.some(op => op.nome === null)) return _operador.length - 1
    return _operador.length
  }

  const getOperatorData = () => {
    return dados?.operador?.map(op => ({
      name: op.nome || 'Aguardando...',
      leads: op.qtd
    })) || [];
  };

  const getCanalData = () => {
    const filteredLeads = getFilteredLeads();

    // Sempre usa os leads filtrados para manter consistência
    if (selectedOperador || periodoSelecionado || filteredLeads.length !== leadsAll.length) {
      // Recalcula os canais baseado nos leads filtrados
      const canalCount = {};
      filteredLeads.forEach(lead => {
        const canal = lead.canal || 'Outros';
        canalCount[canal] = (canalCount[canal] || 0) + 1;
      });

      return Object.entries(canalCount).map(([canal, count]) => ({
        name: canal,
        value: count
      }));
    }

    // Usa dados originais apenas se nenhum filtro estiver aplicado
    return dados?.canal?.map(ca => ({
      name: ca.canal || 'Outros',
      value: ca.qtd
    })) || [];
  };

  const getCampanhaData = () => {
    const filteredLeads = getFilteredLeads();

    // Debug: vamos verificar os dados
    if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
      console.log('Filtered leads for campanha:', filteredLeads);
      console.log('Dados campanha original:', leadsAll);
      console.log('Sample lead campanha field:', filteredLeads[0]?.campanha);
    }

    // Sempre usa os leads filtrados para manter consistência

    // Recalcula as campanhas baseado nos leads filtrados
    const campanhaCount = {};
    filteredLeads.forEach(lead => {
      // Verificar diferentes possíveis nomes do campo
      const campanha = lead.campanha || lead.utm_campaign || lead.campaign || 'Sem campanha';
      campanhaCount[campanha] = (campanhaCount[campanha] || 0) + 1;
    });

    const result = Object.entries(campanhaCount).map(([campanha, count]) => ({
      name: campanha,
      value: count
    }));

    if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
      console.log('Campanha data calculated:', result);
    }

    return result;

    // return originalData;
  };

  const getLeadsPorHoraData = () => {
    const filteredLeads = getFilteredLeads();

    if (isSmallScreen) {
      // Para telas pequenas: agrupa de 3 em 3 horas
      const horasAgrupadas = Array.from({ length: 8 }, (_, index) => {
        const horaInicio = index * 3;
        const horaFim = horaInicio + 2;
        return {
          hora: `${horaInicio.toString().padStart(2, '0')}:00-${horaFim.toString().padStart(2, '0')}:59`,
          leads: 0
        };
      });

      // Conta leads por grupo de 3 horas
      filteredLeads.forEach(lead => {
        if (lead.createdat) {
          const dataLead = new Date(lead.createdat);
          const hora = dataLead.getHours();

          if (hora >= 0 && hora <= 23) {
            const grupoIndex = Math.floor(hora / 3);
            if (grupoIndex < horasAgrupadas.length) {
              horasAgrupadas[grupoIndex].leads++;
            }
          }
        }
      });

      if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
        console.log('Leads por hora agrupadas (tela pequena):', horasAgrupadas);
      }

      return horasAgrupadas;
    } else {
      // Para telas grandes: mantém formato original (24 horas)
      const horasData = Array.from({ length: 24 }, (_, index) => ({
        hora: `${index.toString().padStart(2, '0')}:00`,
        leads: 0
      }));

      // Conta leads por hora baseado no createdat
      filteredLeads.forEach(lead => {
        if (lead.createdat) {
          const dataLead = new Date(lead.createdat);
          const hora = dataLead.getHours();

          if (hora >= 0 && hora <= 23) {
            horasData[hora].leads++;
          }
        }
      });

      if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
        console.log('Leads por hora individuais (tela grande):', horasData);
      }

      return horasData;
    }
  };

  const getLeadsPorDiaSemanaData = () => {
    const filteredLeads = getFilteredLeads();

    // Array com os dias da semana em português
    const diasSemana = [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado'
    ];

    // Inicializa array com todos os dias da semana
    const diasData = diasSemana.map(dia => ({
      dia: isSmallScreen ? dia.substring(0, 3) : dia, // Abrevia para telas pequenas
      diaCompleto: dia,
      leads: 0,
      somaHoras: 0,
      horarioMedio: 0
    }));

    // Conta leads por dia da semana e calcula horário médio baseado no createdat
    filteredLeads.forEach(lead => {
      if (lead.createdat) {
        const dataLead = new Date(lead.createdat);
        const diaSemana = dataLead.getDay(); // 0 = Domingo, 1 = Segunda, etc.
        const hora = dataLead.getHours();
        const minutos = dataLead.getMinutes();
        const horarioDecimal = hora + (minutos / 60); // Converte para decimal para cálculo

        if (diaSemana >= 0 && diaSemana <= 6) {
          diasData[diaSemana].leads++;
          diasData[diaSemana].somaHoras += horarioDecimal;
        }
      }
    });

    // Calcula o horário médio para cada dia
    diasData.forEach(dia => {
      if (dia.leads > 0) {
        dia.horarioMedio = dia.somaHoras / dia.leads;
      }
      // Remove propriedade auxiliar
      delete dia.somaHoras;
    });

    if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
      console.log('Leads por dia da semana com horário médio:', diasData);
    }

    return diasData;
  };

  const getLeadDiaMes = () => {
    const filteredLeads = getFilteredLeads();
    
    // Se não há leads filtrados, retorna array vazio
    if (!filteredLeads.length) return [];

    // Inicializa array com todos os dias do mês (1 a 31)
    const diasData = Array.from({ length: 31 }, (_, index) => {
      const diaDoMes = index + 1;
      
      return {
        dia: diaDoMes,
        diaFormatado: `${diaDoMes.toString().padStart(2, '0')}`,
        leads: 0
      };
    });

    // Conta leads por dia baseado no createdat ou dtdesignacao
    filteredLeads.forEach(lead => {
      let dataLead;

      // Tenta usar createdat primeiro, depois dtdesignacao
      if (lead.createdat) {
        dataLead = new Date(lead.createdat);
      } else if (lead.dtdesignacao) {
        dataLead = new Date(lead.dtdesignacao);
      }

      if (dataLead && !isNaN(dataLead.getTime())) {
        const diaDoMes = dataLead.getDate();
        
        // Soma para o dia correspondente (independente do mês/ano)
        if (diaDoMes >= 1 && diaDoMes <= 31) {
          diasData[diaDoMes - 1].leads++;
        }
      }
    });

    if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
      console.log('Leads por dia (todos os períodos):', diasData);
    }

    return diasData;
  };

  const getLeadsPorTurnoData = () => {
    const filteredLeads = getFilteredLeads();

    // Define os turnos de 
    const turnos = [
      { nome: '(00:00-06:00)', inicio: 0, fim: 5, leads: 0 },
      { nome: '(06:00-09:00)', inicio: 6, fim: 8, leads: 0 },
      { nome: '(09:00-12:00)', inicio: 9, fim: 11, leads: 0 },
      { nome: '(12:00-15:00)', inicio: 12, fim: 14, leads: 0 },
      { nome: '(15:00-18:00)', inicio: 15, fim: 17, leads: 0 },
      { nome: '(18:00-21:00)', inicio: 18, fim: 20, leads: 0 },
      { nome: '(21:00-23:59)', inicio: 21, fim: 23, leads: 0 }
    ];

    // Conta leads por turno baseado no createdat
    filteredLeads.forEach(lead => {
      if (lead.createdat) {
        const dataLead = new Date(lead.createdat);
        const hora = dataLead.getHours();

        turnos.forEach(turno => {
          if (hora >= turno.inicio && hora <= turno.fim) {
            turno.leads++;
          }
        });
      }
    });

    if (Boolean(process.env.REACT_APP_SHOWLOG === 'true')) {
      console.log('Leads por intervalo:', turnos);
    }

    return turnos.map(turno => ({
      turno: isSmallScreen ? `T${turnos.indexOf(turno) + 1}` : turno.nome,
      turnoCompleto: turno.nome,
      leads: turno.leads
    }));
  };


  const getTotalLeads = () => {
    // Sempre usa os leads filtrados para manter consistência
    if (selectedOperador || periodoSelecionado) {
      return getFilteredLeads().length;
    }
    return dados?.total || 0;
  };
  // console.log('qtdOperador', qtdOperador())

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    const element = document.getElementById('charts-container');
    const button = document.getElementById('pdf-button');
    const filtrosContainer = document.getElementById('filtros-container');
    try {
      // Adiciona um pequeno delay para mostrar o loading
      await new Promise(resolve => setTimeout(resolve, 500));
      // Esconde o botão e os filtros antes de capturar
      if (button) {
        button.style.display = 'none';
      }
      if (filtrosContainer) {
        filtrosContainer.style.display = 'none';
      }

      // Captura apenas os cards de gráficos (exclui filtros)
      const cards = element.querySelectorAll('.MuiCard-root:not(#filtros-container .MuiCard-root)');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 295;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      let currentY = margin;
      let isFirstPage = true;

      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];

        // Pula os cards de filtro verificando se são cards de filtro
        const isFilterCard = card.closest('#filtros-container');
        if (isFilterCard) continue;

        // Captura cada card individualmente
        const canvas = await html2canvas(card, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false
        });

        const imgData = canvas.toDataURL('image/png', 1.0);
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        // Verifica se o gráfico cabe na página atual
        if (currentY + imgHeight > pageHeight - margin && !isFirstPage) {
          // Adiciona nova página se não couber
          pdf.addPage();
          currentY = margin;
        }

        // Adiciona o gráfico na posição atual
        pdf.addImage(imgData, 'PNG', margin, currentY, contentWidth, imgHeight);
        currentY += imgHeight + 5; // Adiciona espaço entre gráficos

        isFirstPage = false;
      }

      // Gera nome do arquivo com data/hora
      const agora = new Date();
      const ano = agora.getFullYear();
      const mes = String(agora.getMonth() + 1).padStart(2, '0');
      const dia = String(agora.getDate()).padStart(2, '0');
      const hora = String(agora.getHours()).padStart(2, '0');
      const minuto = String(agora.getMinutes()).padStart(2, '0');

      const nomeArquivo = `relatorio-leads-${ano}-${mes}-${dia}-${hora}h${minuto}.pdf`;
      if (button) {
        button.style.display = 'flex';
      }
      if (filtrosContainer) {
        filtrosContainer.style.display = 'flex';
        filtrosContainer.style.flexDirection = 'row';
        filtrosContainer.style.flexWrap = 'wrap';
        filtrosContainer.style.gap = '2';
        filtrosContainer.style.alignItems = 'stretch';
      }
      // Salva o PDF
      pdf.save(nomeArquivo);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      if (button) {
        button.style.display = 'flex';
      }
      if (filtrosContainer) {
        filtrosContainer.style.display = 'flex';
        filtrosContainer.style.flexDirection = 'row';
        filtrosContainer.style.flexWrap = 'wrap';
        filtrosContainer.style.gap = '2';
        filtrosContainer.style.alignItems = 'stretch';
      }
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <>
      <Container maxWidth="xl" id="charts-container">
        <Box py={3}>
          <Grid container spacing={2}>
            {/* Filtros */}
            <Grid item xs={12} container spacing={2} id="filtros-container" >
              {isAdmin && (
                <Grid item xs={12} md={3}>
                  <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                    <CardContent>
                      <FormControl fullWidth>
                        <InputLabel>Selecionar Operador</InputLabel>
                        <Select
                          value={selectedOperador}
                          label="Selecionar Operador"
                          onChange={handleOperadorChange}
                          sx={{
                            bgcolor: grey[50],
                            height: '50px',
                          }}
                        >
                          <MenuItem value="">Todos os Operadores</MenuItem>
                          {operadores.map((operador) => (
                            <MenuItem key={operador.idoperador} value={operador.idoperador}>
                              {operador.nome}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid item xs={12} md={3}>
                <Card sx={{ bgcolor: grey[50], p: 2, height: '100%' }}>
                  <Typography variant="h4" gutterBottom>
                    Filtrar por Período
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={periodoSelecionado === 'mesAtual'}
                          onChange={handlePeriodoChange}
                          name="mesAtual"
                        />
                      }
                      label="Mês"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={periodoSelecionado === 'ultimos30'}
                          onChange={handlePeriodoChange}
                          name="ultimos30"
                        />
                      }
                      label="30 dias"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={periodoSelecionado === 'trimestre'}
                          onChange={handlePeriodoChange}
                          name="trimestre"
                        />
                      }
                      label="90 dias"
                    />
                  </FormGroup>
                </Card>
              </Grid>

              {/* Summary Cards */}
              <Grid item xs={6} md={3}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Stack direction={'column'} spacing={1}>
                      <Stack spacing={2} direction={'row'} justifyContent="space-between">
                        <Stack direction={'row'} spacing={2} alignItems="center">
                          <Typography variant='h4' color="textSecondary" gutterBottom>
                            {selectedOperador || periodoSelecionado ? 'Filtrados:' : 'Leads:'}
                          </Typography>
                          <Typography variant="h4">{getTotalLeads()}</Typography>
                        </Stack>
                        <Stack>
                          <TrendingUpIcon sx={{ color: green[500] }} />
                        </Stack>
                      </Stack>
                      {(selectedOperador || periodoSelecionado) && (
                        <Stack>
                          {selectedOperador && (
                            <Typography variant="caption" color="textSecondary">
                              Operador: {operadores.find(op => op.idoperador === selectedOperador)?.nome}
                            </Typography>
                          )}
                          {periodoSelecionado && (
                            <Typography variant="caption" color="textSecondary">
                              Período: {getPeriodoTexto()}
                            </Typography>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              {isAdmin && (
                <Grid item xs={6} md={3}>
                  <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction={'row'} spacing={2} alignItems="center">
                          <Stack>
                            <Typography variant='h4' color="textSecondary" gutterBottom>
                              Operadores:
                            </Typography>
                          </Stack>
                          <Stack>
                            {qtdOperador() || 0}
                          </Stack>
                        </Stack>
                        <Stack>
                          <PeopleAltIcon sx={{ color: orange[500] }} />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
            {/* Pie Charts */}
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Distribuição por Fase
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={getStatusData()}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {getStatusData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Distribuição por Situação
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={getSituacaoData()}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {getSituacaoData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Distribuição por Canal
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={getCanalData()}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {getCanalData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Distribuição por Campanha
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={getCampanhaData()}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {getCampanhaData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              {/* Bar Chart */}
              <Grid item
                xs={12}
              >
                {isAdmin && (
                  <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                    <CardContent>
                      <Typography variant="h4" gutterBottom>
                        Leads por Operador
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={getOperatorData()}>
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="leads" fill={orange[500]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </Grid>


              {/* Line Chart - Leads por Hora */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Distribuição de Leads por Hora (24h)
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <LineChart data={getLeadsPorHoraData()}>
                        <XAxis
                          dataKey="hora"
                          tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                          interval={0}
                          angle={isSmallScreen ? -45 : 0}
                          textAnchor={isSmallScreen ? 'end' : 'middle'}
                          height={isSmallScreen ? 80 : 60}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(value) => isSmallScreen ? `Período: ${value}` : `Hora: ${value}`}
                          formatter={(value) => [value, 'Leads']}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="leads"
                          stroke={green[500]}
                          strokeWidth={3}
                          dot={{ fill: green[500], strokeWidth: 2, r: isSmallScreen ? 3 : 4 }}
                          activeDot={{ r: isSmallScreen ? 5 : 6, stroke: green[700], strokeWidth: 2 }}
                          name="Quantidade de Leads"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      {isSmallScreen
                        ? 'Visualização agrupada da distribuição temporal dos leads (períodos de 3 horas)'
                        : 'Visualização da distribuição temporal dos leads ao longo de 24 horas'
                      }
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {/* Bar Chart - Leads por Intervalo */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Leads por Intervalo
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={getLeadsPorTurnoData()}>
                        <XAxis
                          dataKey="turno"
                          tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                          interval={0}
                          angle={isSmallScreen ? -45 : 0}
                          textAnchor={isSmallScreen ? 'end' : 'middle'}
                          height={isSmallScreen ? 80 : 60}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(value, payload) => {
                            // Mostra o nome completo do turno
                            const item = payload?.[0]?.payload;
                            return item ? `${item.turnoCompleto}` : value;
                          }}
                          formatter={(value, name, props) => {
                            // Calcula o total de leads para percentagem
                            const totalLeads = getLeadsPorTurnoData().reduce((sum, item) => sum + item.leads, 0);
                            const percentagem = totalLeads > 0 ? ((value / totalLeads) * 100).toFixed(1) : 0;
                            return [`${value} (${percentagem}%)`, name];
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="leads"
                          fill={blueGrey[500]}
                          radius={[4, 4, 0, 0]}
                          name="Leads"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Distribuição de leads por turnos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Composed Chart - Leads por Dia da Semana com Horário Médio */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Leads por Dia da Semana e Horário Médio
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={getLeadsPorDiaSemanaData()}>
                        <XAxis
                          dataKey="dia"
                          tick={{ fontSize: isSmallScreen ? 10 : 12 }}
                          interval={0}
                          angle={isSmallScreen ? -45 : 0}
                          textAnchor={isSmallScreen ? 'end' : 'middle'}
                          height={isSmallScreen ? 80 : 60}
                        />
                        <YAxis yAxisId="left" orientation="left" />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          domain={[0, 24]}
                          tickFormatter={(value) => `${Math.floor(value).toString().padStart(2, '0')}:${Math.floor((value % 1) * 60).toString().padStart(2, '0')}`}
                        />
                        <Tooltip
                          labelFormatter={(value, payload) => {
                            // Busca o nome completo do dia
                            const item = payload?.[0]?.payload;
                            return item ? `Dia: ${item.diaCompleto}` : `Dia: ${value}`;
                          }}
                          formatter={(value, name) => {
                            if (name === 'Horário Médio') {
                              const horas = Math.floor(value);
                              const minutos = Math.floor((value % 1) * 60);
                              return [`${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`, name];
                            }
                            return [value, name];
                          }}
                        />
                        <Legend />
                        <Bar
                          yAxisId="left"
                          dataKey="leads"
                          fill={blueGrey[500]}
                          radius={[4, 4, 0, 0]}
                          name="Quantidade de Leads"
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="horarioMedio"
                          stroke={red[500]}
                          strokeWidth={3}
                          dot={{ fill: red[500], strokeWidth: 2, r: 5 }}
                          activeDot={{ r: 7, stroke: red[700], strokeWidth: 2 }}
                          name="Horário Médio"
                          connectNulls={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Distribuição de leads por dia da semana e horário médio de recebimento dos leads
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Bar Chart - Leads por Dia do Mês */}
              <Grid item xs={12}>
                <Card sx={{ bgcolor: grey[100], height: '100%' }}>
                  <CardContent>
                    <Typography variant="h4" gutterBottom>
                      Leads por Dia do Mês (1-31)
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={getLeadDiaMes()}>
                        <XAxis
                          dataKey="diaFormatado"
                          tick={{ fontSize: isSmallScreen ? 8 : 10 }}
                          interval={isSmallScreen ? 2 : 1}
                          angle={isSmallScreen ? -45 : 0}
                          textAnchor={isSmallScreen ? 'end' : 'middle'}
                          height={isSmallScreen ? 80 : 60}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(value) => `Dia: ${value}`}
                          formatter={(value, name) => [value, name]}
                        />
                        <Legend />
                        <Bar
                          dataKey="leads"
                          fill={green[500]}
                          radius={[4, 4, 0, 0]}
                          name="Leads"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                      Distribuição de leads por dia do mês 
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Button
          id="pdf-button"
          variant="contained"
          onClick={generatePDF}
          disabled={isGeneratingPDF}
          startIcon={isGeneratingPDF ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
          sx={{
            minWidth: 200,
            height: 48,
            fontSize: '1.1rem'
          }}
        >
          {isGeneratingPDF ? 'Gerando PDF...' : 'Exportar PDF'}
        </Button>
      </Container >
      {/* Backdrop com loading para geração do PDF */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)'
        }}
        open={isGeneratingPDF}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} color="inherit" />
          <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
            Gerando relatório PDF...
          </Typography>
          <Typography variant="body1" color="inherit">
            Aguarde, isso pode levar alguns segundos
          </Typography>
        </Box>
      </Backdrop>
    </>
  );
}
